/**
 * Rheina Cart — drawer, floating button, header bag sync, cart page
 */
(() => {
  const OPEN_EVENT = 'rheina:cart:open';
  const CLOSE_EVENT = 'rheina:cart:close';
  const REFRESH_EVENT = 'rheina:cart:refresh';

  /**
   * @returns {string}
   */
  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  /**
   * @param {number} count
   */
  function updateCounts(count) {
    document.querySelectorAll('[data-rheina-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle('is-empty', count === 0);
    });

    document.querySelectorAll('[data-rheina-cart-count-label]').forEach((el) => {
      el.textContent = count > 0 ? `(${count})` : '';
    });

    document.querySelectorAll('[data-rheina-header-bag-count]').forEach((el) => {
      el.textContent = count > 0 ? String(count) : '';
      el.classList.toggle('is-empty', count === 0);
    });
  }

  /**
   * @param {string} key
   * @param {number} quantity
   * @returns {Promise<object | null>}
   */
  async function changeQuantity(key, quantity) {
    const response = await fetch(`${cartRoot()}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: key, quantity }),
    });

    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  }

  class RheinaCartDrawer extends HTMLElement {
    /** @type {HTMLElement | null} */
    #panel = null;

    /** @type {HTMLButtonElement | null} */
    #fab = null;

    /** @type {boolean} */
    #isOpen = false;

    /** @type {boolean} */
    #busy = false;

    connectedCallback() {
      this.#panel = this.querySelector('[data-rheina-cart-panel]');
      this.#fab = this.querySelector('.rheina-cart__fab');

      this.addEventListener('click', this.#onClick);
      document.addEventListener(OPEN_EVENT, this.#onOpenRequest);
      document.addEventListener(CLOSE_EVENT, this.#onCloseRequest);
      document.addEventListener(REFRESH_EVENT, this.#onRefreshRequest);
      document.addEventListener('cart:update', this.#onCartUpdate);
      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('keydown', this.#onKeydown);

      if (this.#panel) {
        this.#panel.hidden = false;
        this.#panel.setAttribute('aria-hidden', 'true');
      }

      const backdrop = this.querySelector('.rheina-cart__backdrop');
      if (backdrop instanceof HTMLElement) backdrop.hidden = false;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      document.removeEventListener(OPEN_EVENT, this.#onOpenRequest);
      document.removeEventListener(CLOSE_EVENT, this.#onCloseRequest);
      document.removeEventListener(REFRESH_EVENT, this.#onRefreshRequest);
      document.removeEventListener('cart:update', this.#onCartUpdate);
      document.removeEventListener('click', this.#onDocumentClick);
      document.removeEventListener('keydown', this.#onKeydown);
      this.#unlock();
    }

    /** @param {MouseEvent} event */
    #onDocumentClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const openTrigger = target.closest('[data-rheina-cart-open]');
      if (!openTrigger) return;

      // Ignore triggers inside this element — handled by #onClick
      if (this.contains(openTrigger)) return;

      event.preventDefault();
      this.open();
    };

    /** @param {MouseEvent} event */
    #onClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-rheina-cart-open]')) {
        event.preventDefault();
        this.open();
        return;
      }

      if (target.closest('[data-rheina-cart-close]')) {
        const link = target.closest('a[href]');
        if (link instanceof HTMLAnchorElement) {
          const href = link.getAttribute('href');
          if (href && href !== '#') {
            this.close();
            return;
          }
        }
        event.preventDefault();
        this.close();
        return;
      }

      const removeBtn = target.closest('[data-rheina-cart-remove]');
      if (removeBtn instanceof HTMLElement) {
        event.preventDefault();
        const key = removeBtn.getAttribute('data-rheina-cart-remove');
        if (key) this.#changeQuantity(key, 0);
        return;
      }

      const qtyBtn = target.closest('[data-rheina-cart-qty-change]');
      if (qtyBtn instanceof HTMLElement) {
        event.preventDefault();
        const key = qtyBtn.getAttribute('data-key');
        const delta = Number(qtyBtn.getAttribute('data-rheina-cart-qty-change') || 0);
        const item = qtyBtn.closest('[data-rheina-cart-key]');
        const valueEl = item?.querySelector('.rheina-cart__qty-value');
        const current = Number(valueEl?.textContent || 1);
        if (key) this.#changeQuantity(key, Math.max(0, current + delta));
      }
    };

    #onOpenRequest = () => this.open();
    #onCloseRequest = () => this.close();
    #onRefreshRequest = () => this.#refresh();

    /** @param {Event} event */
    #onCartUpdate = (event) => {
      const detail = /** @type {any} */ (event).detail;
      if (!detail) return;
      if (detail.source === 'rheina-cart' || detail.data?.source === 'rheina-cart') return;

      const autoOpen = this.dataset.autoOpen === 'true';
      const resource = detail.resource;
      if (typeof resource?.item_count === 'number') {
        updateCounts(resource.item_count);
      }

      this.#refresh().then(() => {
        if (autoOpen && !this.#isOnCartPage()) this.open();
      });
    };

    /** @param {KeyboardEvent} event */
    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.#isOpen) this.close();
    };

    open() {
      if (this.#isOnCartPage()) return;

      this.#isOpen = true;
      this.classList.add('is-open');
      this.#panel?.setAttribute('aria-hidden', 'false');
      this.#fab?.setAttribute('aria-expanded', 'true');
      this.#lock();

      const closeBtn = this.querySelector('.rheina-cart__close');
      if (closeBtn instanceof HTMLElement) {
        window.setTimeout(() => closeBtn.focus(), 50);
      }
    }

    close() {
      this.#isOpen = false;
      this.classList.remove('is-open');
      this.#panel?.setAttribute('aria-hidden', 'true');
      this.#fab?.setAttribute('aria-expanded', 'false');
      this.#unlock();
    }

    #lock() {
      document.documentElement.classList.add('rheina-cart-open');
    }

    #unlock() {
      document.documentElement.classList.remove('rheina-cart-open');
    }

    #isOnCartPage() {
      return (
        document.body.classList.contains('template-cart') ||
        document.documentElement.classList.contains('template-cart') ||
        /\/cart\/?$/.test(window.location.pathname)
      );
    }

    /**
     * @param {string} key
     * @param {number} quantity
     */
    async #changeQuantity(key, quantity) {
      if (this.#busy) return;
      this.#busy = true;
      this.classList.add('is-loading');

      try {
        const cart = await changeQuantity(key, quantity);
        updateCounts(cart.item_count);
        await this.#refresh();

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            detail: { resource: cart, source: 'rheina-cart' },
            bubbles: true,
          })
        );
      } catch (error) {
        console.error('[Rheina cart]', error);
      } finally {
        this.#busy = false;
        this.classList.remove('is-loading');
      }
    }

    async #refresh() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      try {
        const response = await fetch(`${cartRoot()}?section_id=${sectionId}`);
        if (!response.ok) throw new Error('Section fetch failed');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('rheina-cart-drawer');
        if (!next) return;

        const body = this.querySelector('[data-rheina-cart-body]');
        const footer = this.querySelector('[data-rheina-cart-footer]');
        const nextBody = next.querySelector('[data-rheina-cart-body]');
        const nextFooter = next.querySelector('[data-rheina-cart-footer]');

        if (body && nextBody) body.innerHTML = nextBody.innerHTML;
        if (footer && nextFooter) {
          footer.innerHTML = nextFooter.innerHTML;
          footer.classList.toggle('is-empty', nextFooter.classList.contains('is-empty'));
        }

        const nextCount = next.querySelector('[data-rheina-cart-count]');
        if (nextCount) updateCounts(Number(nextCount.textContent || 0));
      } catch (error) {
        console.error('[Rheina cart refresh]', error);
      }
    }
  }

  class RheinaCartPage {
    /** @type {HTMLElement | null} */
    #root = null;

    /** @type {boolean} */
    #busy = false;

    /** @type {(event: MouseEvent) => void} */
    #onClick;

    constructor() {
      this.#onClick = this.#handleClick.bind(this);
      this.#root = document.querySelector('[data-rheina-cart-page]');
      if (!this.#root) return;

      this.#root.addEventListener('click', this.#onClick);
      document.addEventListener('cart:update', this.#onCartUpdate);
    }

    /** @param {MouseEvent} event */
    #handleClick(event) {
      const target = event.target;
      if (!(target instanceof Element) || !this.#root) return;

      const removeBtn = target.closest('[data-rheina-cart-remove]');
      if (removeBtn instanceof HTMLElement && this.#root.contains(removeBtn)) {
        event.preventDefault();
        const key = removeBtn.getAttribute('data-rheina-cart-remove');
        if (key) this.#changeQuantity(key, 0);
        return;
      }

      const qtyBtn = target.closest('[data-rheina-cart-qty-change]');
      if (qtyBtn instanceof HTMLElement && this.#root.contains(qtyBtn)) {
        event.preventDefault();
        const key = qtyBtn.getAttribute('data-key');
        const delta = Number(qtyBtn.getAttribute('data-rheina-cart-qty-change') || 0);
        const item = qtyBtn.closest('[data-rheina-cart-key]');
        const valueEl = item?.querySelector('.rheina-cart__qty-value');
        const current = Number(valueEl?.textContent || 1);
        if (key) this.#changeQuantity(key, Math.max(0, current + delta));
      }
    }

    #onCartUpdate = (event) => {
      const detail = /** @type {any} */ (event).detail;
      if (!detail) return;
      if (detail.source === 'rheina-cart-page' || detail.data?.source === 'rheina-cart-page') return;
      this.#refresh();
    };

    /**
     * @param {string} key
     * @param {number} quantity
     */
    async #changeQuantity(key, quantity) {
      if (this.#busy || !this.#root) return;
      this.#busy = true;
      this.#root.classList.add('is-loading');

      try {
        const cart = await changeQuantity(key, quantity);
        updateCounts(cart.item_count);
        await this.#refresh();

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            detail: { resource: cart, source: 'rheina-cart-page' },
            bubbles: true,
          })
        );
      } catch (error) {
        console.error('[Rheina cart page]', error);
      } finally {
        this.#busy = false;
        this.#root?.classList.remove('is-loading');
      }
    }

    async #refresh() {
      if (!this.#root) return;
      const sectionId = this.#root.dataset.sectionId;
      if (!sectionId) {
        window.location.reload();
        return;
      }

      try {
        const response = await fetch(`${cartRoot()}cart?section_id=${sectionId}`);
        if (!response.ok) throw new Error('Cart page section fetch failed');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-rheina-cart-page]');
        if (!next) {
          window.location.reload();
          return;
        }

        this.#root.replaceWith(next);
        this.#root = document.querySelector('[data-rheina-cart-page]');
        this.#root?.addEventListener('click', this.#onClick);

        const countEl = next.querySelector('[data-rheina-cart-count]') || document.querySelector('[data-rheina-cart-count]');
        if (countEl) updateCounts(Number(countEl.textContent || 0));
      } catch (error) {
        console.error('[Rheina cart page refresh]', error);
        window.location.reload();
      }
    }
  }

  if (!customElements.get('rheina-cart-drawer')) {
    customElements.define('rheina-cart-drawer', RheinaCartDrawer);
  }

  const initPage = () => {
    if (document.querySelector('[data-rheina-cart-page]')) {
      new RheinaCartPage();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPage);
  } else {
    initPage();
  }

  window.RheinaCart = {
    open: () => document.dispatchEvent(new CustomEvent(OPEN_EVENT)),
    close: () => document.dispatchEvent(new CustomEvent(CLOSE_EVENT)),
    refresh: () => document.dispatchEvent(new CustomEvent(REFRESH_EVENT)),
  };
})();
